import { useState, useEffect } from 'react'
import { Save, AlertTriangle, Pause, Play, Loader2, Mail, Eye, EyeOff } from 'lucide-react'
import { useAdminConfig } from '@/hooks/useAdminConfig'
import { api, UpdateEmailConfigDto } from '@/lib/api'
import { formatUnits, parseUnits } from 'viem'
import toast from 'react-hot-toast'

export function ConfigPage() {
  const { config, isLoading, updateConfig, isUpdating } = useAdminConfig()

  // Local form state - Bridge config
  const [feePercentage, setFeePercentage] = useState(1)
  const [minAmount, setMinAmount] = useState('10')
  const [maxAmount, setMaxAmount] = useState('10000')
  const [largePaymentThreshold, setLargePaymentThreshold] = useState('')
  const [isPaused, setIsPaused] = useState(false)
  const [p2pEnabled, setP2pEnabled] = useState(false)
  const [processingMode, setProcessingMode] = useState<'MANUAL' | 'AUTOMATIC' | 'HYBRID'>('MANUAL')

  // Email config state
  const [emailEnabled, setEmailEnabled] = useState(false)
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState(587)
  const [smtpSecure, setSmtpSecure] = useState(false)
  const [smtpUser, setSmtpUser] = useState('')
  const [smtpPass, setSmtpPass] = useState('')
  const [smtpFromName, setSmtpFromName] = useState('W2USDT Bridge')
  const [smtpFromEmail, setSmtpFromEmail] = useState('noreply@w2usdt.com')
  const [showPassword, setShowPassword] = useState(false)
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false)

  // Sync local state with config when loaded
  useEffect(() => {
    if (config) {
      setFeePercentage(config.feePercentage)
      setIsPaused(config.isPaused)
      setP2pEnabled((config as any).p2pEnabled ?? false)
      setProcessingMode(config.processingMode)
      // Convert from wei to display value
      try {
        setMinAmount(formatUnits(BigInt(config.minBridgeAmount), 18))
        setMaxAmount(formatUnits(BigInt(config.maxBridgeAmount), 18))
        if (config.largePaymentThreshold) {
          setLargePaymentThreshold(formatUnits(BigInt(config.largePaymentThreshold), 18))
        }
      } catch {
        setMinAmount('10')
        setMaxAmount('10000')
      }
      // Email config
      setEmailEnabled(config.emailEnabled || false)
      setSmtpHost(config.smtpHost || '')
      setSmtpPort(config.smtpPort || 587)
      setSmtpSecure(config.smtpSecure || false)
      setSmtpUser(config.smtpUser || '')
      setSmtpFromName(config.smtpFromName || 'W2USDT Bridge')
      setSmtpFromEmail(config.smtpFromEmail || 'noreply@w2usdt.com')
      // No cargamos smtpPass por seguridad
    }
  }, [config])

  const handleSave = () => {
    const data: any = {
      feePercentage,
      minBridgeAmount: parseUnits(minAmount || '10', 18).toString(),
      maxBridgeAmount: parseUnits(maxAmount || '10000', 18).toString(),
      processingMode,
    }
    if (largePaymentThreshold) {
      data.largePaymentThreshold = parseUnits(largePaymentThreshold, 18).toString()
    } else {
      data.largePaymentThreshold = null
    }
    console.log('handleSave called with:', data)
    updateConfig(data)
  }

  const togglePause = () => {
    const newPaused = !isPaused
    setIsPaused(newPaused)
    updateConfig({
      isPaused: newPaused,
      pauseReason: newPaused ? 'Pausado manualmente por admin' : undefined,
    })
  }

  const toggleP2p = () => {
    const next = !p2pEnabled
    setP2pEnabled(next)
    updateConfig({ p2pEnabled: next } as any)
  }

  const handleSaveEmailConfig = async () => {
    setIsUpdatingEmail(true)
    try {
      const data: UpdateEmailConfigDto = {
        emailEnabled,
        smtpHost: smtpHost || undefined,
        smtpPort,
        smtpSecure,
        smtpUser: smtpUser || undefined,
        smtpFromName,
        smtpFromEmail,
      }
      // Solo enviar password si se ha cambiado (no está vacío)
      if (smtpPass) {
        data.smtpPass = smtpPass
      }
      await api.updateEmailConfig(data)
      toast.success('Configuración de email guardada')
      setSmtpPass('') // Limpiar password después de guardar
    } catch (error: any) {
      toast.error(error.message || 'Error al guardar configuración de email')
    } finally {
      setIsUpdatingEmail(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
      </div>
    )
  }

  return (
    <div className="space-y-6 w-full">
      <h1 className="text-xl font-semibold">Configuración del Bridge</h1>

      {/* P2P Feature Flag */}
      <div className="card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-white">Mercado P2P</h3>
            <p className="text-sm text-dark-400 mt-1">
              {p2pEnabled
                ? 'El mercado P2P es visible. Aparece en el menú y las rutas /p2p/* responden.'
                : 'El mercado P2P está oculto. No aparece en el menú y todas las rutas /p2p/* devuelven 404.'}
            </p>
          </div>
          <button
            onClick={toggleP2p}
            disabled={isUpdating}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              p2pEnabled
                ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                : 'bg-accent-500/20 text-accent-400 hover:bg-accent-500/30'
            }`}
          >
            {p2pEnabled ? (
              <>
                <EyeOff className="w-4 h-4" />
                Ocultar P2P
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Mostrar P2P
              </>
            )}
          </button>
        </div>
      </div>

      {/* Pause Control */}
      <div className="card p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-white">Estado del Bridge</h3>
            <p className="text-sm text-dark-400 mt-1">
              {isPaused
                ? 'El bridge está pausado. Los usuarios no pueden hacer nuevas solicitudes.'
                : 'El bridge está activo y funcionando normalmente.'}
            </p>
          </div>
          <button
            onClick={togglePause}
            disabled={isUpdating}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              isPaused
                ? 'bg-accent-500/20 text-accent-400 hover:bg-accent-500/30'
                : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
            }`}
          >
            {isPaused ? (
              <>
                <Play className="w-4 h-4" />
                Activar
              </>
            ) : (
              <>
                <Pause className="w-4 h-4" />
                Pausar
              </>
            )}
          </button>
        </div>
      </div>

      {/* Fee Settings */}
      <div className="card p-5 space-y-4">
        <h3 className="font-medium text-white">Comisiones</h3>

        <div>
          <label className="block text-sm text-dark-400 mb-2">
            Porcentaje de comisión (%)
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="10"
            value={feePercentage}
            onChange={(e) => setFeePercentage(parseFloat(e.target.value) || 0)}
            className="input w-32"
          />
          <p className="text-xs text-dark-500 mt-1">
            Comisión que se cobra por cada transferencia
          </p>
        </div>
      </div>

      {/* Amount Limits */}
      <div className="card p-5 space-y-4">
        <h3 className="font-medium text-white">Límites de Cantidad</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-dark-400 mb-2">
              Mínimo (W2USDT)
            </label>
            <input
              type="number"
              min="0"
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm text-dark-400 mb-2">
              Máximo (W2USDT)
            </label>
            <input
              type="number"
              min="0"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className="input"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm text-dark-400 mb-2">
            Límite para confirmación extra (W2USDT)
          </label>
          <input
            type="number"
            min="0"
            value={largePaymentThreshold}
            onChange={(e) => setLargePaymentThreshold(e.target.value)}
            placeholder="Ej: 1000"
            className="input w-48"
          />
          <p className="text-xs text-dark-500 mt-1">
            Pagos mayores a este monto mostrarán un modal de confirmación extra. Dejar vacío para desactivar.
          </p>
        </div>
      </div>

      {/* Processing Mode */}
      <div className="card p-5 space-y-4">
        <h3 className="font-medium text-white">Modo de Procesamiento</h3>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="processingMode"
              value="MANUAL"
              checked={processingMode === 'MANUAL'}
              onChange={() => setProcessingMode('MANUAL')}
              className="w-4 h-4 text-uni-500 bg-dark-800 border-dark-600 focus:ring-uni-500"
            />
            <div>
              <span className="text-white">Manual</span>
              <p className="text-xs text-dark-500">El admin debe aprobar y confirmar cada solicitud manualmente</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer opacity-50">
            <input
              type="radio"
              name="processingMode"
              value="AUTOMATIC"
              checked={processingMode === 'AUTOMATIC'}
              onChange={() => setProcessingMode('AUTOMATIC')}
              className="w-4 h-4 text-uni-500 bg-dark-800 border-dark-600 focus:ring-uni-500"
              disabled
            />
            <div>
              <span className="text-white">Automático</span>
              <p className="text-xs text-dark-500">Las solicitudes se procesan automáticamente (próximamente)</p>
            </div>
          </label>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-yellow-400 text-sm font-medium">Precaución</p>
          <p className="text-yellow-400/70 text-xs mt-1">
            Los cambios en la configuración afectan inmediatamente a todas las nuevas solicitudes.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={isUpdating}
        className="btn-primary btn-md"
      >
        {isUpdating ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Guardando...
          </>
        ) : (
          <>
            <Save className="w-4 h-4" />
            Guardar Configuración
          </>
        )}
      </button>

      {/* Divider */}
      <div className="border-t border-dark-700 my-8"></div>

      {/* Email Configuration Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-uni-400" />
          <h2 className="text-xl font-semibold">Configuración de Email</h2>
        </div>

        {/* Email Toggle */}
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-white">Notificaciones por Email</h3>
              <p className="text-sm text-dark-400 mt-1">
                {emailEnabled
                  ? 'Los usuarios recibirán emails cuando sus solicitudes sean aprobadas, rechazadas o completadas.'
                  : 'Las notificaciones por email están desactivadas.'}
              </p>
            </div>
            <button
              onClick={() => setEmailEnabled(!emailEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                emailEnabled ? 'bg-uni-500' : 'bg-dark-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  emailEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* SMTP Settings */}
        <div className={`card p-5 space-y-4 ${!emailEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <h3 className="font-medium text-white">Servidor SMTP</h3>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-sm text-dark-400 mb-2">
                Host SMTP
              </label>
              <input
                type="text"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="smtp.gmail.com"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm text-dark-400 mb-2">
                Puerto
              </label>
              <input
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(parseInt(e.target.value) || 587)}
                className="input"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="smtpSecure"
              checked={smtpSecure}
              onChange={(e) => setSmtpSecure(e.target.checked)}
              className="w-4 h-4 text-uni-500 bg-dark-800 border-dark-600 rounded focus:ring-uni-500"
            />
            <label htmlFor="smtpSecure" className="text-sm text-dark-300">
              Usar conexión segura (SSL/TLS)
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-dark-400 mb-2">
                Usuario SMTP
              </label>
              <input
                type="text"
                value={smtpUser}
                onChange={(e) => setSmtpUser(e.target.value)}
                placeholder="tu@email.com"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm text-dark-400 mb-2">
                Contraseña SMTP
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={smtpPass}
                  onChange={(e) => setSmtpPass(e.target.value)}
                  placeholder="••••••••"
                  className="input pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-dark-500 mt-1">
                Dejar vacío para mantener la contraseña actual
              </p>
            </div>
          </div>
        </div>

        {/* From Settings */}
        <div className={`card p-5 space-y-4 ${!emailEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          <h3 className="font-medium text-white">Remitente</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-dark-400 mb-2">
                Nombre del remitente
              </label>
              <input
                type="text"
                value={smtpFromName}
                onChange={(e) => setSmtpFromName(e.target.value)}
                placeholder="W2USDT Bridge"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm text-dark-400 mb-2">
                Email del remitente
              </label>
              <input
                type="email"
                value={smtpFromEmail}
                onChange={(e) => setSmtpFromEmail(e.target.value)}
                placeholder="noreply@w2usdt.com"
                className="input"
              />
            </div>
          </div>
        </div>

        {/* Save Email Config Button */}
        <button
          onClick={handleSaveEmailConfig}
          disabled={isUpdatingEmail}
          className="btn-primary btn-md"
        >
          {isUpdatingEmail ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Mail className="w-4 h-4" />
              Guardar Configuración de Email
            </>
          )}
        </button>
      </div>
    </div>
  )
}
